import React, { useState, useRef, useEffect } from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import PropTypes from 'prop-types';

import { Coupon } from '@carbon/pictograms-react';

import { FormattedMessageWrappedInSpan } from '../../misc';
import { DisabledToActiveButton } from '../../misc/Buttons';
import { PRESALE_EVENT_ON_WITH_ACTION } from '../../../utils/variables';
import SubsectionInFrame from '../../misc/SubsectionInFrame';
import SearchInstructionsDialog from '../dialogs/SearchInstructionsDialog';
import { StartHereOverlay } from '../event/StartHereOverlay';

const PresaleEventInfo = (props) => {
  const { onShowRegularSearch, eventPresaleInfo, warnTextMessage } = props;
  const [showAdditionalSupportText, setShowAdditionalSupportText] = useState(false);
  const [isWarnOverlayOpen, setIsWarnOverlayOpen] = useState(false);
  const [showWarnTextDialog, setShowWarnTextDialog] = useState(false);
  const continueToSearchButtonRef = useRef();

  const shouldShowWarnOverlay = !!warnTextMessage && !isWarnOverlayOpen && !showWarnTextDialog;

  useEffect(() => {
    if (showWarnTextDialog && continueToSearchButtonRef.current) {
      continueToSearchButtonRef.current.focus();
    }
  }, [showWarnTextDialog]);

  useEffect(() => {
    if (warnTextMessage) {
      setIsWarnOverlayOpen(false);
      setShowWarnTextDialog(false);
    }
  }, [warnTextMessage]);

  const warnOverlay = shouldShowWarnOverlay ? (
    <StartHereOverlay
      open={true}
      onClick={() => {
        setIsWarnOverlayOpen(true);
      }}
    />
  ) : null;

  const warnTextDialog = showWarnTextDialog ? (
      <SearchInstructionsDialog
        type="before-search"
        message={warnTextMessage}
        onAccept={() => {
          setShowWarnTextDialog(false);
          onShowRegularSearch();
        }}
        continueButtonRef={continueToSearchButtonRef}
        topPadding={null}
      />
  ) : null;

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      {warnOverlay}
      <SubsectionInFrame
        actionable
        title={
          showWarnTextDialog ? (
            <FormattedMessageWrappedInSpan
              id="searchInstructionsDialog.beforeSearchTitle"
              defaultMessage="Search Instructions"
              className="typography uppercase typography-title_small"
            />
          ) : (
            <FormattedMessageWrappedInSpan
              id="presaleEventInfo.didYouBuyQuestion.title"
              defaultMessage="Presale event"
              className="typography uppercase typography-title_small"
            />
          )
        }
        lastInRow
        topPadding={!showWarnTextDialog}
        center
        gap={20}
        noSidePadding={showWarnTextDialog}
        noBottomPadding={showWarnTextDialog}
      >
        {warnTextDialog}
        {!showWarnTextDialog && (
          <>
                  <Coupon className="pictogram-100" />
        {!showAdditionalSupportText && (
          <FormattedMessageWrappedInSpan
            id="presaleEventInfo.didYouBuyQuestion.question"
            defaultMessage="Did you buy your photos when you signed up for the event?"
          />
        )}
        {showAdditionalSupportText && (
          <div
            data-testid="presale-message"
            dangerouslySetInnerHTML={{
              __html: eventPresaleInfo.presaleMessage,
            }}
          />
        )}
        {!showAdditionalSupportText && (
          <div className="subsection-action-double-buttons">
            <button
              className="warning-btn full-width subsection-action-button subsection-action-button_no-border-radius"
              onClick={() => {
                if (!!warnTextMessage && !showWarnTextDialog) {
                  setShowWarnTextDialog(true);
                  return;
                }
                onShowRegularSearch();
              }}
            >
              <FormattedMessageWrappedInSpan
                id="presaleEventInfo.didYouBuyQuestion.answerNo"
                defaultMessage="No"
                className="typography bold-text typography-title_xsmall"
              />
            </button>
            <button
              className="primary-new full-width subsection-action-button subsection-action-button_no-border-radius"
              onClick={() => setShowAdditionalSupportText(true)}
            >
              <FormattedMessageWrappedInSpan
                id="presaleEventInfo.didYouBuyQuestion.answerYes"
                defaultMessage="Yes"
                className="typography bold-text typography-title_xsmall"
              />
            </button>
          </div>
        )}
        {showAdditionalSupportText && (
          <DisabledToActiveButton
            className="primary-new subsection-action-button "
            onClick={() => {
              if (!!warnTextMessage && !showWarnTextDialog) {
                setShowWarnTextDialog(true);
                return;
              }
              onShowRegularSearch();
            }}
          >
            {eventPresaleInfo.presale === PRESALE_EVENT_ON_WITH_ACTION ? (
              <FormattedMessageWrappedInSpan
                id="presaleEventInfo.buttons.continue"
                defaultMessage="Continue"
                className="typography bold-text typography-title_xsmall"
              />
            ) : (
              <FormattedMessageWrappedInSpan
                id="presaleEventInfo.buttons.continueAnyway"
                defaultMessage="Continue anyway"
                className="typography bold-text typography-title_xsmall"
              />
            )}
          </DisabledToActiveButton>
        )}
          </>
        )}
      </SubsectionInFrame>
    </div>
  );
};

PresaleEventInfo.propTypes = {
  onShowRegularSearch: PropTypes.func,
  eventPresaleInfo: PropTypes.object,
  warnTextMessage: PropTypes.string,
};

export { PresaleEventInfo };

export default createFragmentContainer(PresaleEventInfo, {
  eventPresaleInfo: graphql`
    fragment PresaleEventInfo_eventPresaleInfo on Event {
      presaleMessage
      presale
    }
  `,
});
